import React, { useState, useRef, useMemo } from 'react';
import axios from 'axios';
import AdvancedAudio from './AdvancedAudio';
import AdvancedAudioReadOnly from './AdvancedAudioReadOnly';
import { Link } from 'react-router-dom';
import Modal from '../../components/Modal';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { getTitleById, titleUpdate } from '../../slices/titleSlice';
import Loading from '../../components/Loading';

export const getTrackUrl = (id) => {
    return axios
        .request({
            url: `/authorized/assets/${id}/signedurl`,
            method: 'GET'
        })
        .then((resp) => resp.data);
};

export const updateTrack = (id, data) => {
    return axios.request({
        url: `/authorized/assets/${id}`,
        method: 'PATCH',
        data
    });
};

export const deleteTrack = (id) => {
    return axios.request({
        url: `/authorized/assets/${id}`,
        method: 'DELETE'
    });
};

const TitleAudio = () => {
    const dispatch = useDispatch();
    const { data } = useSelector((state) => state.title);
    const session = useSelector((state) => state.session);
    const [refreshingStatus, setRefreshingStatus] = useState(false);

    // const [allTracks, setAllTracks] = useState([]);
    const [currentTrack, setCurrentTrack] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [isDeletingAll, setIsDeletingAll] = useState(false);
    const [isReordering, setIsReordering] = React.useState(false);

    const _audio = useRef(new Audio());

    const handleRefreshData = async () => {
        setRefreshingStatus(true);
        let newData = await getTitleById(data.id);
        dispatch(titleUpdate(newData));
        setRefreshingStatus(false);
    };

    const handleSetCurrentTrack = async (track) => {
        if (currentTrack) _audio.current.pause();

        if (!currentTrack || track.id !== currentTrack.id) {
            let signedUrl = await getTrackUrl(track.id);
            _audio.current.position = 0;
            _audio.current.src = signedUrl;
        }

        _audio.current.play();
        setCurrentTrack(track);
        setPlaying(true);
    };

    const handlePauseAudio = () => {
        _audio.current.pause();
        setPlaying(false);
    };

    const handleDeleteAll = async (e) => {
        e.preventDefault();
        try {
            if (window.confirm('Are you sure you want to delete all audio files? This action cannot be undone.')) {
                setIsDeletingAll(true);
                await Promise.all(allTracks.map((track) => deleteTrack(track.id)));
                dispatch(titleUpdate({ assets: data.assets.filter((a) => a.assettype !== 'audio') }));
                toast.success('All audio files were deleted.');
                setIsDeletingAll(false);
            }
        } catch (err) {
            setIsDeletingAll(false);
            toast.error('There was a problem deleting all of the files.');
        }
    };

    const canEdit = session.enableEditing && !session.isReadOnly;

    const allTracks = useMemo(() => data?.assets?.filter((a) => a.assettype === 'audio') || [], [data.assets]);

    return (
        <>
            {canEdit && (
                <>
                    <p>
                        Below are the audio files associated with this title. Please use the area below to update, add,
                        or remove audio files, and ensure that all audio files you submit meet our{' '}
                        <a href={process.env.REACT_APP_TECH_SPECS_DOC} target="_blank" rel="noopener noreferrer">
                            technical specifications
                        </a>
                        .
                    </p>
                    <p>
                        Your changes will save in Passport as you make them, but will not go live on INaudio’s Partner
                        network until you <Link to={`/catalog/${data.id}/submit`}>Review & Submit</Link>.
                    </p>
                    <div className="flash flash-light mb-4">
                        <p>
                            <strong>Reminder!</strong>
                            <br /> How you order your files is how your audiobook will play. INaudio will publish your
                            audiobook in the order arranged here.
                        </p>
                    </div>
                </>
            )}

            <div className="txt-right">
                {canEdit && !!allTracks.length && (
                    <>
                        <button
                            className="btn btn-danger btn-sm"
                            onClick={handleDeleteAll}
                            disabled={isDeletingAll || isReordering}
                            type="button"
                        >
                            {isDeletingAll ? 'Deleting Files...' : 'Delete All Files'}
                        </button>
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={handleRefreshData}
                            disabled={isReordering}
                        >
                            Refresh Audio Status
                        </button>
                    </>
                )}
            </div>

            <div className="mb-3">
                {refreshingStatus && <Loading />}
                {!refreshingStatus && !canEdit && (
                    <AdvancedAudioReadOnly
                        onPlay={handleSetCurrentTrack}
                        onPause={handlePauseAudio}
                        currentTrack={currentTrack}
                        isPlaying={playing}
                    />
                )}

                {!refreshingStatus && canEdit && (
                    <div className="afm">
                        <div className="afm-toc">
                            <AdvancedAudio
                                onPlay={handleSetCurrentTrack}
                                onPause={handlePauseAudio}
                                currentTrack={currentTrack}
                                isPlaying={playing}
                                handleRefetch={handleRefreshData}
                                isReordering={isReordering}
                                setIsReordering={(val) => setIsReordering(val)}
                            />
                        </div>
                    </div>
                )}
            </div>

            <Modal
                label="Changes have been saved."
                visible={showSubmitModal}
                onClose={(e) => {
                    e.preventDefault();

                    setShowSubmitModal(false);
                }}
            >
                <p className="text-center mb-4">
                    Your changes have been saved in Passport. Note that your changes will not go live in INaudio's
                    partner network until you review and submit all changes here.
                </p>
                <div className="text-center">
                    <Link className="btn btn-primary" to={`/catalog/${data.id}/submit`}>
                        Review &amp; Submit
                    </Link>
                    <button
                        className="btn btn-link"
                        onClick={(e) => {
                            e.preventDefault();
                            setShowSubmitModal(false);
                        }}
                        type="button"
                    >
                        Close
                    </button>
                </div>
            </Modal>
        </>
    );
};

export default TitleAudio;
